/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	ChangeDetectionStrategy,
	Component,
	OnDestroy,
	OnInit,
	inject,
	signal,
	ChangeDetectorRef,
	ViewChild,
	ElementRef,
	AfterViewInit
} from '@angular/core';
import { BigFormService, IndexedDbService } from '@flexus/core';
import { TabsData } from '@flexus/ui-elements';
import { Subject, Observable } from 'rxjs';
import { takeUntil} from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { toObservable } from '@angular/core/rxjs-interop';

export interface StoredDocument {
	id: string;
	documentType: string;
	documentLabel: string;
	file?: File;
	base64: string;
	thumbnail: string | null;
	timestamp: number;
	fileName?: string;
}

@Component({
	selector: 'flx-supporting-document-file-preview',
	templateUrl: './supporting-document-file-preview.component.html',
	styleUrls: ['./supporting-document-file-preview.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class FLXSupportingDocumentFilePreviewComponent implements OnInit, OnDestroy, AfterViewInit {
	// Dependencies
	private readonly bf = inject(BigFormService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly indexedDb = inject(IndexedDbService);
	private readonly destroy$ = new Subject<void>();
	private readonly sanitizer = inject(DomSanitizer);

	// Signals
	readonly documents = signal<StoredDocument[]>([]);
	readonly Math = Math; // Expose Math for template
	readonly isLoading = signal<boolean>(false);
	readonly tabs = signal<TabsData[]>([]);
	readonly currentTab$ = signal<string | null>(null);
	readonly currentTabObservable$: Observable<string | null>;
	readonly zoomLevel = signal<number>(1);
	readonly panX = signal<number>(0);
	readonly panY = signal<number>(0);
	readonly imageRotation = signal<number>(0);
	readonly isDragging = signal<boolean>(false);
	readonly dragStartX = signal<number>(0);
	readonly dragStartY = signal<number>(0);

	// ViewChild for PDF canvas
	@ViewChild('pdfCanvas', { static: false }) pdfCanvas!: ElementRef<HTMLCanvasElement>;
	@ViewChild('imageContainer', { static: false }) imageContainer!: ElementRef<HTMLDivElement>;

	// PDF.js variables
	private pdfDoc: any = null;
	readonly pdfPageNum = signal<number>(1);
	readonly pdfTotalPages = signal<number>(0);
	private pdfjsLib: any = null;

	constructor() {
		// Convert signal to observable for tabs component
		this.currentTabObservable$ = toObservable(this.currentTab$);
	}

	ngOnInit(): void {
		// Watch for changes to tenant_document_config to reload documents when they're uploaded
		const configControl = this.bf.bigForm.get('tenant_document_config');
		if (configControl) {
			configControl.valueChanges
				.pipe(takeUntil(this.destroy$))
				.subscribe(() => {
					// Reload documents when config changes (e.g., when documents are uploaded)
					this.loadDocuments();
				});
		}
	}

	ngAfterViewInit(): void {
		// Initialize PDF.js worker
		this.initializePdfJs().then(() => {
			// Load documents after PDF.js is initialized and view is ready
			this.loadDocuments();
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private async initializePdfJs(): Promise<void> {
		try {
			this.pdfjsLib = this.normalizePdfJsImport(await import('pdfjs-dist'));
			const workerOptions = this.pdfjsLib?.GlobalWorkerOptions;
			if (workerOptions && !workerOptions.workerSrc) {
				// Prefer a locally hosted worker shipped with the app (avoids CSP / CDN availability issues in prod,
				// and avoids relying on bundler-specific worker URL resolution).
				// Use document.baseURI so it works when app is deployed under a sub-path.
				try {
					workerOptions.workerSrc = new URL('assets/pdfjs/pdf.worker.min.js', document.baseURI).toString();
				} catch {
					workerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
				}
			}
		} catch (error) {
			console.error('Error initializing PDF.js:', error);
		}
	}

	/**
	 * pdfjs-dist can be loaded as ESM/CJS and bundlers may wrap exports under `default`.
	 * We normalize the dynamic import so downstream usage is stable.
	 */
	private normalizePdfJsImport(mod: any): any {
		if (mod?.getDocument && mod?.GlobalWorkerOptions) return mod;
		if (mod?.default?.getDocument && mod?.default?.GlobalWorkerOptions) return mod.default;
		if (mod?.pdfjsLib?.getDocument && mod?.pdfjsLib?.GlobalWorkerOptions) return mod.pdfjsLib;
		return mod;
	}

	private async loadDocuments(): Promise<void> {
		this.isLoading.set(true);

		try {
			const configControl = this.bf.bigForm.get('tenant_document_config');
			if (!configControl) {
				this.isLoading.set(false);
				this.cdr.markForCheck();
				return;
			}

			const config = configControl.value;
			if (!config || !config.required_documents || !Array.isArray(config.required_documents)) {
				this.isLoading.set(false);
				this.cdr.markForCheck();
				return;
			}

			const requiredDocs = config.required_documents;
			const loadedDocuments: StoredDocument[] = [];
			const tabData: TabsData[] = [];

			for (const doc of requiredDocs) {
				const key = `supporting_doc_${doc.type}`;
				try {
					// Try IndexedDB first
					const indexedDoc = await this.indexedDb.data_store.get(key);
					if (indexedDoc?.data) {
						const storedDoc: StoredDocument = {
							...indexedDoc.data,
							documentLabel: doc.label || indexedDoc.data.documentLabel || doc.type,
							fileName: indexedDoc.data.file?.name || indexedDoc.data.fileName || `${doc.type}.pdf`
						};
						loadedDocuments.push(storedDoc);
						tabData.push({
							display: doc.label || doc.type,
							targetId: doc.type,
							show: true
						});
					}
				} catch (error) {
					console.error(`Error loading document ${key}:`, error);
				}
			}

			this.documents.set(loadedDocuments);
			this.tabs.set(tabData);

			if (tabData.length > 0) {
				this.currentTab$.set(tabData[0].targetId);
				// Load the first document if available
				if (loadedDocuments.length > 0) {
					// Wait for view to initialize before loading PDF
					setTimeout(() => {
						this.loadCurrentDocument();
					}, 100);
				}
			}
		} catch (error) {
			console.error('Error loading documents:', error);
		} finally {
			this.isLoading.set(false);
			this.cdr.markForCheck();
		}
	}

	private async loadCurrentDocument(): Promise<void> {
		const currentTab = this.currentTab$();
		if (!currentTab) return;

		const document = this.documents().find(doc => doc.documentType === currentTab);
		if (!document) return;

		const fileExtension = (document.fileName || '').split('.').pop()?.toLowerCase() || '';
		const isPdf = fileExtension === 'pdf';
		const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);

		if (isPdf) {
			// Wait for PDF.js to be initialized
			if (!this.pdfjsLib) {
				await this.initializePdfJs();
			}
			// Wait a bit for the canvas to be available in the DOM
			await new Promise(resolve => setTimeout(resolve, 100));
			await this.loadPdf(document);
		} else if (isImage) {
			// Image will be loaded via template binding
			this.cdr.markForCheck();
		}
	}

	private async loadPdf(document: StoredDocument): Promise<void> {
		if (!this.pdfjsLib || !this.pdfCanvas) return;

		try {
			// Convert base64 to array buffer
			// Handle both data URL format and raw base64
			let base64Data = document.base64;
			if (base64Data.startsWith('data:')) {
				base64Data = base64Data.replace(/^data:.*?;base64,/, '');
			}
			
			const byteCharacters = atob(base64Data);
			const byteNumbers = new Array(byteCharacters.length);
			for (let i = 0; i < byteCharacters.length; i++) {
				byteNumbers[i] = byteCharacters.charCodeAt(i);
			}
			const byteArray = new Uint8Array(byteNumbers);
			const arrayBuffer = byteArray.buffer;

			const loadingTask = this.pdfjsLib.getDocument({ data: arrayBuffer });
			this.pdfDoc = await loadingTask.promise;
			this.pdfTotalPages.set(this.pdfDoc.numPages);
			this.pdfPageNum.set(1);

			await this.renderPdfPage(1);
		} catch (error) {
			console.error('Error loading PDF:', error);
		}
	}

	private async renderPdfPage(pageNum: number): Promise<void> {
		if (!this.pdfDoc || !this.pdfjsLib) return;

		// Wait for canvas to be available
		if (!this.pdfCanvas?.nativeElement) {
			await new Promise(resolve => setTimeout(resolve, 100));
			if (!this.pdfCanvas?.nativeElement) {
				console.warn('PDF canvas not available yet');
				return;
			}
		}

		try {
			const page = await this.pdfDoc.getPage(pageNum);
			const viewport = page.getViewport({ scale: this.zoomLevel() });
			const canvas = this.pdfCanvas.nativeElement;
			const context = canvas.getContext('2d');

			if (!context) {
				console.error('Could not get canvas context');
				return;
			}

			canvas.height = viewport.height;
			canvas.width = viewport.width;

			const renderContext = {
				canvasContext: context,
				viewport: viewport
			};

			await page.render(renderContext).promise;
			this.cdr.markForCheck();
		} catch (error) {
			console.error('Error rendering PDF page:', error);
		}
	}

	getCurrentDocument(): StoredDocument | null {
		const currentTab = this.currentTab$();
		if (!currentTab) return null;
		return this.documents().find(doc => doc.documentType === currentTab) || null;
	}

	getCurrentDocumentUrl(): any {
		const document = this.getCurrentDocument();
		if (!document) return null;

		const fileExtension = (document.fileName || '').split('.').pop()?.toLowerCase() || '';
		const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);

		if (isImage) {
			// Handle both data URL format and raw base64
			const base64Data = document.base64;
			if (base64Data.startsWith('data:')) {
				// Already a data URL, use it directly
				return this.sanitizer.bypassSecurityTrustUrl(base64Data);
			} else {
				// Raw base64, convert to data URL
				const mimeType = fileExtension === 'jpg' ? 'image/jpeg' : `image/${fileExtension}`;
				return this.sanitizer.bypassSecurityTrustUrl(`data:${mimeType};base64,${base64Data}`);
			}
		}

		return null;
	}

	getCurrentDocumentMimeType(): string {
		const document = this.getCurrentDocument();
		if (!document) return '';

		const fileExtension = (document.fileName || '').split('.').pop()?.toLowerCase() || '';
		if (fileExtension === 'pdf') return 'pdf';
		if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) return 'image';
		return '';
	}

	handleTab(tabInfo: any): void {
		if (tabInfo?.target) {
			this.currentTab$.set(tabInfo.target);
			this.zoomLevel.set(1);
			this.panX.set(0);
			this.panY.set(0);
			this.imageRotation.set(0);
			// Wait a bit for the tab content to render before loading document
			setTimeout(() => {
				this.loadCurrentDocument();
			}, 50);
		}
	}

	zoomIn(): void {
		const newZoom = Math.min(this.zoomLevel() * 1.2, 5);
		this.zoomLevel.set(newZoom);
		if (this.getCurrentDocumentMimeType() === 'pdf') {
			this.renderPdfPage(this.pdfPageNum());
		}
		this.cdr.markForCheck();
	}

	zoomOut(): void {
		const newZoom = Math.max(this.zoomLevel() * 0.8, 0.5);
		this.zoomLevel.set(newZoom);
		if (this.getCurrentDocumentMimeType() === 'pdf') {
			this.renderPdfPage(this.pdfPageNum());
		}
		this.cdr.markForCheck();
	}

	resetZoom(): void {
		this.zoomLevel.set(1);
		this.panX.set(0);
		this.panY.set(0);
		this.imageRotation.set(0);
		if (this.getCurrentDocumentMimeType() === 'pdf') {
			this.renderPdfPage(this.pdfPageNum());
		}
		this.cdr.markForCheck();
	}

	onMouseDown(event: MouseEvent): void {
		if (event.button !== 0) return; // Only left mouse button
		this.isDragging.set(true);
		this.dragStartX.set(event.clientX - this.panX());
		this.dragStartY.set(event.clientY - this.panY());
		event.preventDefault();
	}

	onMouseMove(event: MouseEvent): void {
		if (!this.isDragging()) return;
		this.panX.set(event.clientX - this.dragStartX());
		this.panY.set(event.clientY - this.dragStartY());
		this.cdr.markForCheck();
	}

	onMouseUp(): void {
		this.isDragging.set(false);
	}

	onMouseLeave(): void {
		this.isDragging.set(false);
	}

	onWheel(event: WheelEvent): void {
		event.preventDefault();
		const delta = event.deltaY > 0 ? 0.9 : 1.1;
		const newZoom = Math.max(0.5, Math.min(5, this.zoomLevel() * delta));
		this.zoomLevel.set(newZoom);
		if (this.getCurrentDocumentMimeType() === 'pdf') {
			this.renderPdfPage(this.pdfPageNum());
		}
		this.cdr.markForCheck();
	}

	previousPage(): void {
		if (this.pdfPageNum() > 1) {
			this.pdfPageNum.set(this.pdfPageNum() - 1);
			this.renderPdfPage(this.pdfPageNum());
		}
	}

	nextPage(): void {
		if (this.pdfPageNum() < this.pdfTotalPages()) {
			this.pdfPageNum.set(this.pdfPageNum() + 1);
			this.renderPdfPage(this.pdfPageNum());
		}
	}

	getImageTransform(): string {
		return `translate(${this.panX()}px, ${this.panY()}px) scale(${this.zoomLevel()})`;
	}

	rotateLeft(): void {
		this.imageRotation.set((this.imageRotation() - 90 + 360) % 360);
		this.cdr.markForCheck();
	}

	rotateRight(): void {
		this.imageRotation.set((this.imageRotation() + 90) % 360);
		this.cdr.markForCheck();
	}
}

